/**
 * What monitoring already exists in Azure, per subscription.
 *
 * NOVA does not create these rules — this is an inventory, and its real value
 * is the empty rows: a subscription with production workloads and zero alert
 * rules is a gap worth seeing. Subscriptions with no rules are therefore listed
 * explicitly rather than being dropped from the table.
 */

import { Badge, DataGrid } from "@norican/react";

import type { AlertRuleGroup, CloudSubscription } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatCount } from "./status";

interface AlertRow {
  id: string;
  name: string;
  metricAlerts: number;
  logAlerts: number;
  activityLogAlerts: number;
  total: number;
  enabled: number;
  workloads: number;
}

interface AlertInventoryProps {
  alertRules: AlertRuleGroup[];
  subscriptions: CloudSubscription[];
}

export function AlertInventory({ alertRules, subscriptions }: AlertInventoryProps) {
  const byId = new Map(alertRules.map((group) => [group.subscription_id, group]));
  const rows: AlertRow[] = subscriptions
    .filter((subscription) => subscription.reachable)
    .map((subscription) => {
      const group = byId.get(subscription.subscription_id);
      return {
        id: subscription.subscription_id,
        name: subscription.name,
        metricAlerts: group?.metric_alerts ?? 0,
        logAlerts: group?.log_alerts ?? 0,
        activityLogAlerts: group?.activity_log_alerts ?? 0,
        total: group?.total ?? 0,
        enabled: group?.enabled ?? 0,
        workloads: subscription.workloads,
      };
    })
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));

  const uncovered = rows.filter((row) => row.total === 0 && row.workloads > 0);
  const total = rows.reduce((sum, row) => sum + row.total, 0);

  return (
    <PanelCard
      title="Existing alert rules"
      caption={
        uncovered.length > 0
          ? `${formatCount(total)} rules · ${uncovered.length} subscriptions with workloads but no rules`
          : `${formatCount(total)} rules across ${rows.length} subscriptions`
      }
      padding="none"
    >
      <DataGrid<AlertRow>
        data={rows}
        getRowId={(row) => row.id}
        density="compact"
        aria-label="Azure alert rules per subscription"
        columns={[
          {
            id: "name",
            header: "Subscription",
            accessor: (row) => row.name,
            sortable: true,
            cell: (row) => <span className="text-fg font-medium">{row.name}</span>,
          },
          {
            id: "metric",
            header: "Metric alerts",
            align: "end",
            accessor: (row) => row.metricAlerts,
            sortable: true,
            cell: (row) => <span className="text-fg tabular-nums">{row.metricAlerts}</span>,
          },
          {
            id: "log",
            header: "Log alerts",
            align: "end",
            accessor: (row) => row.logAlerts,
            sortable: true,
            cell: (row) => <span className="text-fg tabular-nums">{row.logAlerts}</span>,
          },
          {
            id: "activity",
            header: "Activity log",
            align: "end",
            accessor: (row) => row.activityLogAlerts,
            sortable: true,
            cell: (row) => <span className="text-fg tabular-nums">{row.activityLogAlerts}</span>,
          },
          {
            id: "enabled",
            header: "Enabled",
            align: "end",
            accessor: (row) => row.enabled,
            sortable: true,
            cell: (row) => (
              <span className="text-fg-muted tabular-nums">
                {row.enabled} / {row.total}
              </span>
            ),
          },
          {
            id: "coverage",
            header: "Coverage",
            accessor: (row) => row.total,
            sortable: true,
            cell: (row) =>
              row.total > 0 ? (
                <Badge tone="success" variant="subtle" size="xs">
                  {row.total} rules
                </Badge>
              ) : row.workloads > 0 ? (
                <Badge tone="warning" variant="subtle" size="xs">
                  {row.workloads} workloads, no rules
                </Badge>
              ) : (
                <Badge tone="neutral" variant="subtle" size="xs">
                  No workloads
                </Badge>
              ),
          },
        ]}
      />
    </PanelCard>
  );
}
